/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import clsx from 'clsx';
import { Chip, Box } from '@material-ui/core';
import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { getSubmissionStatusById, SubmissionStatus } from '../../models/SubmissionStatus';
import Tooltip from './Tooltip';

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  chip: {
    [theme.breakpoints.up('xl2')]: {
      fontSize: '18px',
    },
    minWidth: 0,
    maxWidth: '100%',
  },
  chipLabel: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
    overflow: 'hidden',
  },
  DRAFT: {
    backgroundColor: theme.palette.statusChip.DRAFT.main,
    color: theme.palette.statusChip.DRAFT.contrastText,
  },
  VALID_DRAFT: {
    backgroundColor: theme.palette.statusChip.DRAFT.main,
    color: theme.palette.statusChip.DRAFT.contrastText,
  },
  READY_TO_SIGN: {
    backgroundColor: theme.palette.statusChip.READY_TO_SIGN.main,
    color: theme.palette.statusChip.READY_TO_SIGN.contrastText,
  },
  VALID_READY_TO_SIGN: {
    backgroundColor: theme.palette.statusChip.VALID_READY_TO_SIGN.main,
    color: theme.palette.statusChip.VALID_READY_TO_SIGN.contrastText,
  },
  SENT_TO_SIGN: {
    backgroundColor: theme.palette.statusChip.SENT_TO_SIGN.main,
    color: theme.palette.statusChip.SENT_TO_SIGN.contrastText,
  },
  SENDING_FAILED: {
    backgroundColor: theme.palette.statusChip.SENDING_FAILED.main,
    color: theme.palette.statusChip.SENDING_FAILED.contrastText,
  },
  SIGNING_FAILED: {
    backgroundColor: theme.palette.statusChip.SIGNING_FAILED.main,
    color: theme.palette.statusChip.SIGNING_FAILED.contrastText,
  },
  RESIGNING_FAILED: {
    backgroundColor: theme.palette.statusChip.RESIGNING_FAILED.main,
    color: theme.palette.statusChip.RESIGNING_FAILED.contrastText,
  },
  SIGNED: {
    backgroundColor: theme.palette.statusChip.READY_TO_SEND.main,
    color: theme.palette.statusChip.READY_TO_SEND.contrastText,
  },
  SENT_TO_FILING: {
    backgroundColor: theme.palette.statusChip.SENT_TO_FILING.main,
    color: theme.palette.statusChip.SENT_TO_FILING.contrastText,
  },
  READY_TO_PAY: {
    backgroundColor: theme.palette.statusChip.READY_TO_PAY.main,
    color: theme.palette.statusChip.READY_TO_PAY.contrastText,
  },
  PAID: {
    backgroundColor: theme.palette.statusChip.PAID.main,
    color: theme.palette.statusChip.PAID.contrastText,
  },
  SENT: {
    backgroundColor: theme.palette.statusChip.SENT.main,
    color: theme.palette.statusChip.SENT.contrastText,
  },
  ACCEPTED: {
    backgroundColor: theme.palette.statusChip.SENT.main,
    color: theme.palette.statusChip.SENT.contrastText,
  },
  EXPIRED: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.statusChip.SENDING_FAILED.contrastText,
  },
  PENDING: {
    backgroundColor: theme.palette.statusChip.DRAFT.main,
    color: theme.palette.statusChip.DRAFT.contrastText,
  },
  DECLINED: {
    backgroundColor: theme.palette.statusChip.SENDING_FAILED.main,
    color: theme.palette.statusChip.SENDING_FAILED.contrastText,
  },
  default: {
    backgroundColor: theme.palette.statusChip.default.main,
    color: theme.palette.statusChip.default.contrastText,
  },
  validated: {
    backgroundColor: theme.palette.statusChip.validBackground,
    '&:after': {
      content: '""',
      top: '40%',
      left: '50%',
      width: '0.95rem',
      height: '.55rem',
      position: 'absolute',
      transform: 'translate(-50%, -50%) rotate(-45deg)',
      borderLeft: '0.15rem solid white',
      borderBottom: '0.15rem solid white',
    },
  },
  invalidated: {
    backgroundColor: theme.palette.statusChip.invalidBackground,
    '&:after': {
      content: '""',
      top: '0.25rem',
      left: '0.75rem',
      height: '1.1rem',
      position: 'absolute',
      transform: 'rotate(45deg)',
      borderLeft: '0.15rem solid white',
    },
    '&:before': {
      content: '""',
      top: '0.25rem',
      left: '0.75rem',
      height: '1.1rem',
      position: 'absolute',
      transform: 'rotate(-45deg)',
      borderLeft: '0.15rem solid white',
    },
  },
}));

function getHelpInfo(status, t) {
  const helpInfoByStatus = {
    [SubmissionStatus.DRAFT.id]: t('minimum.requirements.not.met'),
    [SubmissionStatus.VALID_DRAFT.id]: t('minimum.requirements.completed'),
    [SubmissionStatus.READY_TO_SIGN.id]: t('submission.ready.to.sign'),
    [SubmissionStatus.VALID_READY_TO_SIGN.id]: t('submission.valid.ready.to.sign'),
    [SubmissionStatus.READY_TO_SEND.id]: t('submission.ready.for.sending'),
    [SubmissionStatus.SENT.id]: t('submission.has.been.sent'),
    [SubmissionStatus.SENT_TO_SIGN.id]: t('submission.has.been.sent.to.sign'),
    [SubmissionStatus.SENT_TO_FILING.id]: t('submission.has.been.sent.to.filing'),
    [SubmissionStatus.READY_TO_PAY.id]: t('submission.ready.to.pay'),
    [SubmissionStatus.PAID.id]: t('submission.paid.processing'),
    [SubmissionStatus.RESIGNING_FAILED.id]: t('submission.status.resigning.failed'),
  };

  return helpInfoByStatus[status] ?? t(status);
}

export function ChipLabel({ status, addHiddenInfo, hasError, isFilterDraftValid = false, chipBadgeStyle = {} }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const hasDraftBadge =
    status === SubmissionStatus.VALID_DRAFT.id ||
    status === SubmissionStatus.VALID_READY_TO_SIGN.id ||
    isFilterDraftValid;

  return (
    <Box display="flex" aria-label={t('submission.status')}>
      <span className={classes.chipLabel}>{t(getSubmissionStatusById(status)?.i18nKey) || t(status)}</span>
      {hasDraftBadge && (
        <span className={clsx([classes.validated, chipBadgeStyle])} role="img" aria-label={t('Validated')} />
      )}
      {hasError && <span className={classes.invalidated} role="img" aria-label={t('Submission with error')} />}
      {addHiddenInfo && <span className={classes.visuallyHidden}>{getHelpInfo(status, t)}</span>}
    </Box>
  );
}

/**
 * Status badge for a submission.
 * @param status - submission status
 * @param showTooltip - shows tooltip on the chip with error text or the help info defined per status
 * @param addHiddenInfo - adds the help info to a hidden span
 * @param errorText - error messages from the submission
 * @returns {JSX.Element}
 */
export default function StatusChip({ status, showTooltip = true, addHiddenInfo = false, errorText }) {
  const classes = useStyles();
  const { t } = useTranslation();

  const hasError = !!errorText;

  const CustomizedChip = (
    <Chip
      label={<ChipLabel status={status} addHiddenInfo={addHiddenInfo} hasError={hasError} />}
      className={clsx(classes.chip, classes[status ?? 'default'])}
    />
  );

  if (showTooltip) {
    return (
      <Tooltip className="body-row-tooltip" title={getHelpInfo(errorText || status, t)}>
        <span role="button" tabIndex="0" aria-label={status}>
          {CustomizedChip}
        </span>
      </Tooltip>
    );
  }

  return CustomizedChip;
}
